import { type ChangeEvent, type FormEvent } from "react"
import { useForm, usePage } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { LocationSectionScreen } from "@/features/staff/location-section-screen"
import { t } from "@/lib/i18n"

const tSection = (key: string) =>
  t(`ui.react.pages.staff.location.manage.location.sections.${key}`)

type TipSettings = {
  suggestedPercentages: number[]
  distribution: { waiters: number; shared: number }
  /** The policy's cap, so the field row and the cap cannot drift apart. */
  maxSuggestions: number
}

type Props = { tips: TipSettings }

/**
 * Tip suggestions and the split, on the location page.
 *
 * Explicit submit rather than inline commit: these numbers change what every
 * diner is shown and what the staff is owed, which is the "critical resource
 * configured in one transaction" case the design system reserves ceremony for.
 *
 * Two things this card does deliberately:
 *
 * 1. **It previews the diner's row rather than describing it**, and previews what
 *    is being typed rather than what is saved. The immovable 0% shows up as a
 *    real chip in that row, so the manager sees the rule instead of reading a
 *    sentence about it. The list is rebuilt here rather than read from the
 *    server precisely because it has to track the fields live; the rule that it
 *    mirrors `Location::TipPolicy` is held by specs on both sides.
 * 2. **It reports the split's total continuously.** A split that does not reach
 *    100 is refused server-side and would otherwise bounce back with the old
 *    numbers restored and no explanation. Showing the sum as it is typed turns
 *    that into something the manager fixes before saving.
 */
function TipsCard({ tips }: { tips: TipSettings }) {
  const padded = [...tips.suggestedPercentages, ...Array(tips.maxSuggestions).fill("")].slice(0, tips.maxSuggestions)

  const { data, setData, patch, processing } = useForm({
    tips: {
      suggested_percentages: padded.map(String),
      distribution: {
        waiters: String(tips.distribution.waiters),
        shared: String(tips.distribution.shared),
      },
    },
  })

  const suggestions = data.tips.suggested_percentages
  const { waiters, shared } = data.tips.distribution

  // Mirrors Location::TipPolicy.normalize_percentages so the preview shows what
  // will actually be stored, not what was typed.
  const previewOptions = [
    0,
    ...Array.from(
      new Set(
        suggestions
          .map((value) => Number.parseInt(value, 10))
          .filter((value) => Number.isFinite(value) && value > 0 && value <= 100)
      )
    ).sort((a, b) => a - b),
  ]

  const splitTotal = (Number.parseInt(waiters, 10) || 0) + (Number.parseInt(shared, 10) || 0)
  const splitBalanced = splitTotal === 100

  const setSuggestion = (index: number, value: string) => {
    const next = [...suggestions]
    next[index] = value.replace(/[^\d]/g, "")
    setData("tips", { ...data.tips, suggested_percentages: next })
  }

  const setShare = (key: "waiters" | "shared", value: string) => {
    setData("tips", {
      ...data.tips,
      distribution: { ...data.tips.distribution, [key]: value.replace(/[^\d]/g, "") },
    })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    patch("/manage/location/tips", { preserveScroll: true })
  }

  return (
    <Card>
      <CardContent>
        <form onSubmit={submit} className="space-y-6" data-testid="location-tips-form">
          <section className="space-y-2">
            <Label htmlFor="tip-suggestion-0">{tSection("tips_suggestions_label")}</Label>
            <div className="grid grid-cols-4 gap-2">
              {suggestions.map((value, index) => (
                <Input
                  key={index}
                  id={`tip-suggestion-${index}`}
                  inputMode="numeric"
                  value={value}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setSuggestion(index, event.target.value)}
                  className="tabular-nums"
                  aria-label={`${tSection("tips_suggestions_label")} ${index + 1}`}
                  data-testid={`tip-suggestion-${index}`}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{tSection("tips_suggestions_hint")}</p>
          </section>

          <section className="space-y-2">
            <Label>{tSection("tips_preview_label")}</Label>
            <div className="flex flex-wrap gap-2" data-testid="tip-preview">
              {previewOptions.map((percentage) => (
                <span
                  key={percentage}
                  className="rounded-md border border-border px-2 py-1 text-xs font-medium tabular-nums"
                  data-testid={`tip-preview-${percentage}`}
                >
                  {percentage}%
                </span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{tSection("tips_zero_note")}</p>
          </section>

          <section className="space-y-2">
            <Label htmlFor="tip-share-waiters">{tSection("tips_distribution_label")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Input
                  id="tip-share-waiters"
                  inputMode="numeric"
                  value={waiters}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setShare("waiters", event.target.value)}
                  className="tabular-nums"
                  aria-label={tSection("tips_distribution_waiters")}
                  data-testid="tip-share-waiters"
                />
                <p className="text-xs text-muted-foreground">{tSection("tips_distribution_waiters")}</p>
              </div>
              <div className="space-y-1">
                <Input
                  id="tip-share-shared"
                  inputMode="numeric"
                  value={shared}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setShare("shared", event.target.value)}
                  className="tabular-nums"
                  aria-label={tSection("tips_distribution_shared")}
                  data-testid="tip-share-shared"
                />
                <p className="text-xs text-muted-foreground">{tSection("tips_distribution_shared_hint")}</p>
              </div>
            </div>
            {/* Warning, not destructive: nothing is broken, the numbers just do
                not add up yet. Carried by text as well as color, because color
                alone is not an accessible signal. */}
            <p
              className={splitBalanced ? "text-xs text-muted-foreground" : "text-xs text-warning"}
              data-testid="tip-share-total"
            >
              {splitBalanced ? tSection("tips_distribution_hint") : tSection("tips_distribution_invalid")}
            </p>
          </section>

          <Button type="submit" disabled={processing || !splitBalanced} data-testid="tip-submit">
            {tSection("tips_submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * The regime this restaurant operates under, and the rates broken out on a
 * diner's receipt.
 *
 * Explicit submit rather than inline commit. Regime and rates are one decision,
 * not four independent fields — a regime saved without its rates would claim an
 * arithmetic direction for numbers that no longer match it — and a rate ends up
 * in a tax filing, which is the "critical resource configured in one
 * transaction" case `.claude/rules/system-design.md` reserves ceremony for. It
 * is also what keeps the audit row readable as one tax decision.
 *
 * **The card never re-derives whether prices include tax.** That rule lives in
 * `Location::TaxRegime` and arrives as `pricesIncludeTax`; a second copy of it
 * in TypeScript is precisely how the sentence a manager reads and the
 * arithmetic a diner is charged stop agreeing.
 *
 * The regime is stated rather than picked, because the server currently offers
 * exactly one: nothing adds a forward regime's tax to what a diner is charged,
 * so offering the other would promise a diner payment that never happens.
 */

export default function StaffLocationManageLocationTips() {
  const { tips } = usePage<Props>().props

  return (
    <LocationSectionScreen title={tSection("tips_title")} description={tSection("tips_description")}>
      <TipsCard tips={tips} />
    </LocationSectionScreen>
  )
}
